/**
 * Main JavaScript Entry Point
 * * Ce fichier est bundlé par Rollup avec tree-shaking automatique.
 */

// ============================================================================
// IMPORTS
// ============================================================================
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { initAnimations, animateEntrance } from './animations/gsap-generic';
import 'instant.page';
import Swiper from 'swiper/bundle';
import Alpine from 'alpinejs';
import Sketchfab from '@sketchfab/viewer-api';

// OPTION 1 : Import de l'API Sketchfab via NPM (décommente si tu as fait le npm install)
// import Sketchfab from '@sketchfab/viewer-api';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

window.Swiper = Swiper;
window.Alpine = Alpine;

function initTheme() {
    // Strict session cookie override
    if (!sessionStorage.getItem('prehome_seen')) {
        // Clear cookie if no sessionStorage present (fresh session)
        document.cookie = "prehome_seen=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        const prehomeEl = document.querySelector('#pre-home');
        if (prehomeEl) {
            prehomeEl.style.display = 'block';
            document.body.classList.add('has-prehome');
        }
    }

    initAnimations();

    // ========================================================================
    // NAVIGATION MOBILE (Burger + Panneau GSAP)
    // ========================================================================
    const burgerBtn = document.querySelector('.mobile-burger-btn');
    const panel = document.querySelector('.mobile-navigation-panel');

    if (burgerBtn && panel) {
        const line1 = burgerBtn.querySelector('.line-1');
        const line2 = burgerBtn.querySelector('.line-2');
        const line3 = burgerBtn.querySelector('.line-3');
        const menuLinks = panel.querySelectorAll('.panel-center ul li');
        
        let isMenuOpen = false;

        // Configuration du Timeline GSAP pour l'ouverture
        const menuTl = gsap.timeline({ paused: true });

        menuTl
            .to(line2, { 
                opacity: 0, 
                scaleX: 0, 
                duration: 0.25, 
                ease: "power2.inOut" 
            }, 0)
            .to(line1, { 
                y: 8, 
                rotate: 45, 
                duration: 0.3, 
                ease: "power2.inOut" 
            }, 0)
            .to(line3, { 
                y: -8, 
                rotate: -45, 
                duration: 0.3, 
                ease: "power2.inOut" 
            }, 0)
            .to(panel, { 
                x: 360, 
                duration: 0.45, 
                ease: "power3.out" 
            }, 0)
            .to(menuLinks, { 
                opacity: 1, 
                y: 0, 
                stagger: 0.07, 
                duration: 0.4, 
                ease: "power2.out" 
            }, "-=0.2");

        // Toggle du menu au clic sur le burger
        burgerBtn.addEventListener('click', () => {
            if (!isMenuOpen) {
                menuTl.play();
                isMenuOpen = true;
                document.body.classList.add('mobile-menu-open');
            } else {
                menuTl.reverse();
                isMenuOpen = false;
                document.body.classList.remove('mobile-menu-open');
            }
        });

        // Fermeture automatique du menu au clic sur un lien interne
        panel.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', () => {
                if (isMenuOpen) {
                    menuTl.reverse();
                    isMenuOpen = false;
                    document.body.classList.remove('mobile-menu-open');
                }
            });
        });

        // Fermeture du menu si clic en dehors du panneau et du bouton burger
        document.addEventListener('click', (e) => {
            if (isMenuOpen && !panel.contains(e.target) && !burgerBtn.contains(e.target)) {
                menuTl.reverse();
                isMenuOpen = false;
                document.body.classList.remove('mobile-menu-open');
            }
        });
    }

    // ========================================================================
    // ANIMATIONS DU SLIDER (Animations fluides, lévitation et entrée)
    // ========================================================================
    let activeLevitationTween = null;
    let activeSwiperMetaTimeline = null;

    function animateSwiperMetaElements(slideEl) {
        if (activeSwiperMetaTimeline) {
            activeSwiperMetaTimeline.kill();
        }

        const num = slideEl.querySelector('.product-slide-num');
        const chars = slideEl.querySelectorAll('.product-slide-title .char');
        const metaWrapper = slideEl.querySelector('.product-slide-meta');

        if (!metaWrapper) return;

        // Set parent to visible, reset children opacity
        gsap.set(metaWrapper, { opacity: 1 });
        gsap.set(num, { opacity: 0, y: 15 });
        gsap.set(chars, { opacity: 0 });

        activeSwiperMetaTimeline = gsap.timeline();

        activeSwiperMetaTimeline.to(num, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: "power2.out"
        });

        activeSwiperMetaTimeline.to(chars, {
            opacity: 1,
            stagger: 0.03,
            duration: 0.3,
            ease: "back.out(1.7)"
        }, "-=0.2");
    }
    
    function startActiveSlideLevitation(swiper) {
        if (activeLevitationTween) {
            activeLevitationTween.kill();
            activeLevitationTween = null;
        }

        const slides = swiper.el.querySelectorAll('.product-slide');
        const allImages = swiper.el.querySelectorAll('.product-slide-img');
        gsap.to(allImages, { y: 0, duration: 0.6, ease: "power2.out" });

        // Hide other slides meta
        slides.forEach((s, idx) => {
            if (idx !== swiper.activeIndex) {
                const metaWrapper = s.querySelector('.product-slide-meta');
                if (metaWrapper) {
                    gsap.to(metaWrapper, { opacity: 0, duration: 0.3, ease: "power2.out" });
                }
            }
        });

        const activeSlide = slides[swiper.activeIndex];
        if (!activeSlide) return;

        const activeImg = activeSlide.querySelector('.product-slide-img');
        if (activeImg) {
            activeLevitationTween = gsap.to(activeImg, {
                y: -12, duration: 2.4, repeat: -1, yoyo: true, ease: "sine.inOut"
            });
        }

        // Animate meta elements for active slide
        animateSwiperMetaElements(activeSlide);
    }

    function playSliderEntrance(swiperInstance) {
        const sliderContainer = document.querySelector('.product-slider-container');
        if (!sliderContainer) return;

        const slideLinks = sliderContainer.querySelectorAll('.product-slide-link');
        const uiElements = document.querySelectorAll('#home-slider .swiper-button-next, #home-slider .swiper-button-prev, #home-slider .scroll-indicator');

        // Configuration de l'état initial des éléments
        gsap.set(sliderContainer, { opacity: 0 });
        gsap.set(uiElements, { opacity: 0, y: 15 });

        const tl = gsap.timeline({
            onComplete: () => {
                if (swiperInstance) startActiveSlideLevitation(swiperInstance);
            }
        });

        // Animation de glissement et d'apparition du slider
        tl.to(sliderContainer, { opacity: 1, duration: 0.1 });
        tl.from(sliderContainer, { x: "100%", duration: 1.2, ease: "power3.out" }, 0);
        tl.from(slideLinks, {
            scaleX: 1.2, skewX: -2, opacity: 0, x: -100, stagger: 0.1, duration: 1.0,
            ease: "power2.out", clearProps: "transform,opacity,scaleX,skewX,x"
        }, "-=0.8");
        
        // Animation d'apparition des éléments d'interface (boutons navigation, scroll icon)
        tl.to(uiElements, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
            clearProps: "transform,opacity"
        }, "-=0.6");
    }

    // Lance l'animation d'entrée du slider seulement après le chargement de l'image active
    function initSliderWhenReady(swiperInstance, initialSlide) {
        const swiperEl = document.querySelector('.product-swiper');
        if (!swiperEl) return;

        // Split titles into individual characters (spans) for stagger animation
        const titles = swiperEl.querySelectorAll('.product-slide-title');
        titles.forEach(title => {
            const text = title.textContent.trim();
            title.innerHTML = '';
            
            const words = text.split(/\s+/);
            words.forEach((word, wordIndex) => {
                const wordSpan = document.createElement('span');
                wordSpan.classList.add('word');
                wordSpan.style.display = 'inline-block';
                wordSpan.style.whiteSpace = 'nowrap';
                
                for (let char of word) {
                    const charSpan = document.createElement('span');
                    charSpan.classList.add('char');
                    charSpan.textContent = char;
                    wordSpan.appendChild(charSpan);
                }
                
                title.appendChild(wordSpan);
                
                if (wordIndex < words.length - 1) {
                    title.appendChild(document.createTextNode(' '));
                }
            });
        });

        // Hide all slides meta initially
        const metas = swiperEl.querySelectorAll('.product-slide-meta');
        gsap.set(metas, { opacity: 0 });

        const slides = swiperEl.querySelectorAll('.product-slide');
        const activeSlide = slides[initialSlide];
        if (!activeSlide) {
            playSliderEntrance(swiperInstance);
            return;
        }

        const activeImg = activeSlide.querySelector('.product-slide-img');

        // Callback à exécuter une seule fois
        let hasInitialized = false;
        const triggerEntrance = () => {
            if (hasInitialized) return;
            hasInitialized = true;
            playSliderEntrance(swiperInstance);
        };

        if (activeImg) {
            if (activeImg.complete) {
                // Déjà chargée
                triggerEntrance();
            } else {
                // Attente du chargement complet ou en erreur
                activeImg.addEventListener('load', triggerEntrance);
                activeImg.addEventListener('error', triggerEntrance);
                
                // Timeout de sécurité (1.5 secondes)
                setTimeout(triggerEntrance, 1500);
            }
        } else {
            // Pas d'image, affichage immédiat
            triggerEntrance();
        }
    }

    // --- Initialisation du Swiper principal ---
    const swiperEl = document.querySelector('.product-swiper');
    let productSwiper = null;
    
    if (swiperEl && typeof Swiper !== 'undefined') {
        const initialSlide = parseInt(swiperEl.getAttribute('data-initial-slide') || '0', 10);
        productSwiper = new Swiper(swiperEl, {
            slidesPerView: 1.9,
            centeredSlides: true,
            spaceBetween: 20,
            initialSlide: initialSlide,
            speed: 400,
            navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
            breakpoints: {
                768: { slidesPerView: 2, spaceBetween: 60 }
            },
            on: {
                slideChange: function() { startActiveSlideLevitation(this); }
            }
        });
    }

    // ========================================================================
    // HYBRID HORIZONTAL/VERTICAL SCROLL (ScrollTrigger)
    // ========================================================================
    let activeScrollLevitationTween = null;
    let currentActiveIndex = -1;
    let activeMetaTimeline = null;
    let isDemoScrolling = true;
    let updateActiveSlideGlobal = null;

    function initHomeScrollTriggerWhenReady() {
        const container = document.querySelector('#home-scroll-container');
        if (!container) return;

        // Split product titles into individual characters (spans) for stagger animation
        const titles = container.querySelectorAll('.product-slide-title');
        titles.forEach(title => {
            const text = title.textContent.trim();
            title.innerHTML = '';
            
            const words = text.split(/\s+/);
            words.forEach((word, wordIndex) => {
                const wordSpan = document.createElement('span');
                wordSpan.classList.add('word');
                wordSpan.style.display = 'inline-block';
                wordSpan.style.whiteSpace = 'nowrap';
                
                for (let char of word) {
                    const charSpan = document.createElement('span');
                    charSpan.classList.add('char');
                    charSpan.textContent = char;
                    wordSpan.appendChild(charSpan);
                }
                
                title.appendChild(wordSpan);
                
                if (wordIndex < words.length - 1) {
                    title.appendChild(document.createTextNode(' '));
                }
            });
        });

        // Find the first product image
        const isMobile = window.innerWidth < 768;
        const firstImgSelector = isMobile ? '.scroll-product-item:not(.clone) .product-slide-img' : '.scroll-product-item .product-slide-img';
        const firstImg = container.querySelector(firstImgSelector);
        
        let hasInitialized = false;
        const triggerEntrance = () => {
            if (hasInitialized) return;
            hasInitialized = true;
            playHomeScrollEntrance();
        };

        if (firstImg) {
            if (firstImg.complete) {
                triggerEntrance();
            } else {
                firstImg.addEventListener('load', triggerEntrance);
                firstImg.addEventListener('error', triggerEntrance);
                setTimeout(triggerEntrance, 1500); // safety timeout
            }
        } else {
            triggerEntrance();
        }
    }

    function playHomeScrollEntrance() {
        const container = document.querySelector('#home-scroll-container');
        if (!container) return;

        const isMobile = window.innerWidth < 768;
        const slidesSelector = isMobile ? '.scroll-product-item:not(.clone)' : '.scroll-product-item';
        const slides = container.querySelectorAll(slidesSelector);
        const slideLinks = Array.from(slides).map(slide => slide.querySelector('.product-slide-link')).filter(Boolean);
        const uiElements = document.querySelectorAll('#home-slider .slider-nav-btn-wrapper, #home-slider .scroll-indicator');
        const numSlides = slides.length;

        // Lock scroll during entrance animation
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        const isDesktop = window.innerWidth >= 768;
        const startWidth = isDesktop ? "20vw" : "30vw";
        const endWidth = isDesktop ? "50vw" : "80vw";

        isDemoScrolling = true;

        // State initial
        // - container starts off-screen to the right (x: window.innerWidth)
        // - slides start clustered closer together (width: startWidth) and links slightly scaled (0.8)
        gsap.set(container, { x: window.innerWidth });
        gsap.set(slides, { width: startWidth });
        gsap.set(slideLinks, { scale: 0.8 });
        gsap.set(uiElements, { opacity: 0, y: 15 });

        const tl = gsap.timeline({
            onComplete: () => {
                // Unlock scroll
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
            }
        });

        // Slide in container from right to left with opacity fade-in
        tl.to(container, { opacity: 1, x: 0, duration: 1.2, ease: "power2.out" });
        
        // Expand slide widths (creates the spacing out effect)
        tl.to(slides, {
            width: endWidth,
            duration: 1.2,
            ease: "power2.out",
            clearProps: "width"
        }, 0);

        // Scale up link containers to inactive default size
        tl.to(slideLinks, {
            scale: 0.95,
            duration: 1.2,
            ease: "power2.out",
            clearProps: "scale"
        }, 0);
        
        tl.to(uiElements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            clearProps: "transform,opacity"
        }, "-=0.6");

        // Stagger global title and intro
        const globalTitle = document.querySelector('#home-slider .scroll-slider-title');
        const globalIntro = document.querySelector('#home-slider .scroll-slider-intro');

        if (globalTitle) {
            gsap.set(globalTitle, { opacity: 0, y: 20 });
            tl.to(globalTitle, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }, 0.2); // Animate alongside slide expansion with slight delay
        }

        if (globalIntro) {
            gsap.set(globalIntro, { opacity: 0, y: 20 });
            tl.to(globalIntro, {
                opacity: 1,
                y: 0,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }, "-=0.5");
        }

        // Chain programmatic scroll to Product 1
        tl.add(() => {
            // Clear the transform set by the timeline x tween on the container
            // so ScrollTrigger can control it during programmatic scroll
            gsap.set(container, { clearProps: "transform" });

            // Now setup ScrollTrigger (it will measure slides at their final width!)
            const mainTrigger = setupHomeScrollTrigger();
            
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                isDemoScrolling = false;
                currentActiveIndex = 0;
                const firstRealSlide = container.querySelector('.scroll-product-item:not(.clone)');
                if (firstRealSlide) {
                    firstRealSlide.classList.add('active');
                    
                    const activeImg = firstRealSlide.querySelector('.product-slide-img');
                    if (activeImg) {
                        gsap.to(activeImg, { scale: 1.08, duration: 0.6, ease: "power2.out" });
                        if (activeScrollLevitationTween) {
                            activeScrollLevitationTween.kill();
                        }
                        activeScrollLevitationTween = gsap.to(activeImg, {
                            y: -12,
                            duration: 2.4,
                            repeat: -1,
                            yoyo: true,
                            ease: "sine.inOut"
                        });
                    }

                    const num = firstRealSlide.querySelector('.product-slide-num');
                    const chars = firstRealSlide.querySelectorAll('.product-slide-title .char');
                    const metaWrapper = firstRealSlide.querySelector('.product-slide-meta');
                    if (metaWrapper) {
                        if (activeMetaTimeline) {
                            activeMetaTimeline.kill();
                        }
                        gsap.set(metaWrapper, { opacity: 1 });
                        gsap.set(num, { opacity: 0, y: 15 });
                        gsap.set(chars, { opacity: 0 });

                        activeMetaTimeline = gsap.timeline();
                        activeMetaTimeline.to(num, {
                            opacity: 1,
                            y: 0,
                            duration: 0.4,
                            ease: "power2.out"
                        });
                        activeMetaTimeline.to(chars, {
                            opacity: 1,
                            stagger: 0.03,
                            duration: 0.3,
                            ease: "back.out(1.7)"
                        }, "-=0.2");
                    }
                }
            } else if (mainTrigger && numSlides > 1) {
                const scrollRange = mainTrigger.end - mainTrigger.start;
                const targetScrollY = mainTrigger.start + (1 / (numSlides - 1)) * scrollRange;

                // Perform the scrollTo Product 1
                gsap.to(window, {
                    scrollTo: { y: targetScrollY, autoKill: false },
                    duration: 0.8,
                    ease: "power2.out",
                    onComplete: () => {
                        // Wait 0.4s for ScrollTrigger scrub to catch up and stabilize
                        gsap.delayedCall(0.4, () => {
                            isDemoScrolling = false;
                            if (typeof updateActiveSlideGlobal === 'function') {
                                updateActiveSlideGlobal(true);
                            }
                        });
                    }
                });
            } else {
                isDemoScrolling = false;
                if (typeof updateActiveSlideGlobal === 'function') {
                    updateActiveSlideGlobal(true);
                }
            }
        });
    }

    function setupHomeScrollTrigger() {
        const scrollWrapper = document.querySelector('#home-scroll-wrapper');
        const container = document.querySelector('#home-scroll-container');
        if (!scrollWrapper || !container) return null;

        const isMobile = window.innerWidth < 768;
        const slidesSelector = isMobile ? '.scroll-product-item:not(.clone)' : '.scroll-product-item';
        const slides = container.querySelectorAll(slidesSelector);
        const numSlides = slides.length;

        const getScrollAmount = () => {
            return container.scrollWidth - window.innerWidth;
        };

        function animateMetaElements(slideEl) {
            if (activeMetaTimeline) {
                activeMetaTimeline.kill();
            }

            const num = slideEl.querySelector('.product-slide-num');
            const chars = slideEl.querySelectorAll('.product-slide-title .char');
            const metaWrapper = slideEl.querySelector('.product-slide-meta');

            if (!metaWrapper) return;

            // Set parent to visible, reset children opacity
            gsap.set(metaWrapper, { opacity: 1 });
            gsap.set(num, { opacity: 0, y: 15 });
            gsap.set(chars, { opacity: 0 });

            activeMetaTimeline = gsap.timeline();

            activeMetaTimeline.to(num, {
                opacity: 1,
                y: 0,
                duration: 0.4,
                ease: "power2.out"
            });

            activeMetaTimeline.to(chars, {
                opacity: 1,
                stagger: 0.03,
                duration: 0.3,
                ease: "back.out(1.7)"
            }, "-=0.2");
        }

        function startScrollSlideLevitation(slideEl) {
            if (activeScrollLevitationTween) {
                activeScrollLevitationTween.kill();
                activeScrollLevitationTween = null;
            }

            // Reset other slide images to y: 0, scale: 0.95
            slides.forEach(s => {
                if (s !== slideEl) {
                    const img = s.querySelector('.product-slide-img');
                    if (img) {
                        gsap.to(img, { y: 0, scale: 0.95, duration: 0.6, ease: "power2.out" });
                    }
                    const metaWrapper = s.querySelector('.product-slide-meta');
                    if (metaWrapper) {
                        gsap.to(metaWrapper, { opacity: 0, duration: 0.3, ease: "power2.out" });
                    }
                }
            });

            // If demo scrolling is active, reset slideEl as well and return early
            if (isDemoScrolling) {
                const img = slideEl.querySelector('.product-slide-img');
                if (img) {
                    gsap.to(img, { y: 0, scale: 0.95, duration: 0.6, ease: "power2.out" });
                }
                const metaWrapper = slideEl.querySelector('.product-slide-meta');
                if (metaWrapper) {
                    gsap.to(metaWrapper, { opacity: 0, duration: 0.3, ease: "power2.out" });
                }
                return;
            }

            const activeImg = slideEl.querySelector('.product-slide-img');
            if (activeImg) {
                // Active image scale 1.08 + float animation
                gsap.to(activeImg, { scale: 1.08, duration: 0.6, ease: "power2.out" });
                activeScrollLevitationTween = gsap.to(activeImg, {
                    y: -12,
                    duration: 2.4,
                    repeat: -1,
                    yoyo: true,
                    ease: "sine.inOut"
                });
            }

            // Animate meta elements for active slide
            animateMetaElements(slideEl);
        }

        function updateActiveSlide(force = false) {
            if (ScrollTrigger.isRefreshing) return;
            let closestIndex = 0;
            let minDistance = Infinity;
            const viewportCenter = window.innerWidth / 2;

            slides.forEach((slide, index) => {
                const rect = slide.getBoundingClientRect();
                const slideCenter = rect.left + rect.width / 2;
                const distance = Math.abs(slideCenter - viewportCenter);

                if (distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                }
            });

            if (closestIndex !== currentActiveIndex || force) {
                currentActiveIndex = closestIndex;
                
                slides.forEach((slide, index) => {
                    if (index === closestIndex) {
                        slide.classList.add('active');
                        startScrollSlideLevitation(slide);
                    } else {
                        slide.classList.remove('active');
                    }
                });
            }
        }

        // Expose updateActiveSlide to the outer scope
        updateActiveSlideGlobal = updateActiveSlide;

        // Horizontal scroll animation + pinning
        const mainTrigger = ScrollTrigger.create({
            trigger: scrollWrapper,
            pin: true,
            start: "top top",
            end: () => "+=" + getScrollAmount(),
            scrub: 0.4, // Faster scrub for a more responsive feel
            invalidateOnRefresh: true,
            animation: gsap.to(container, {
                x: () => -getScrollAmount(),
                ease: "none"
            }),
            onUpdate: () => {
                updateActiveSlide();
            },
            onRefresh: () => {
                updateActiveSlide();
            }
        });

        // Initialize first slide state
        updateActiveSlide();

        // Nav arrow click listeners
        const prevBtn = document.querySelector('.scroll-button-prev');
        const nextBtn = document.querySelector('.scroll-button-next');

        if (prevBtn && nextBtn && numSlides > 1) {
            prevBtn.addEventListener('click', () => {
                if (isDemoScrolling) return;
                const targetIndex = Math.max(0, currentActiveIndex - 1);
                const scrollRange = mainTrigger.end - mainTrigger.start;
                const targetScrollY = mainTrigger.start + (targetIndex / (numSlides - 1)) * scrollRange;
                
                gsap.to(window, {
                    scrollTo: targetScrollY,
                    duration: 0.5,
                    ease: "power1.inOut"
                });
            });

            nextBtn.addEventListener('click', () => {
                if (isDemoScrolling) return;
                const targetIndex = Math.min(numSlides - 1, currentActiveIndex + 1);
                const scrollRange = mainTrigger.end - mainTrigger.start;
                const targetScrollY = mainTrigger.start + (targetIndex / (numSlides - 1)) * scrollRange;
                
                gsap.to(window, {
                    scrollTo: targetScrollY,
                    duration: 0.5,
                    ease: "power1.inOut"
                });
            });
        }

        // Scroll indicator click listener
        const scrollIndicator = document.querySelector('#home-slider .scroll-indicator');
        if (scrollIndicator) {
            scrollIndicator.addEventListener('click', () => {
                const targetSection = document.querySelector('#intro-content');
                if (targetSection) {
                    gsap.to(window, {
                        scrollTo: targetSection,
                        duration: 1.0,
                        ease: "power1.inOut"
                    });
                }
            });
        }

        // Initialize other page animations now that the main pinned ScrollTrigger is created
        initHomeAnimations();
        initProductAnimations();
        initQuizAnimations();
        initCardsAnimations();

        // Force a recalculation of all ScrollTriggers on the page
        ScrollTrigger.refresh();

        return mainTrigger;
    }

    // --- Gestion de la transition du Carton Pré-home ---
    const prehome = document.querySelector('#pre-home');
    const explorerBtn = document.querySelector('#pre-home .btn-cta');

    if (prehome && getComputedStyle(prehome).display !== 'none') {
        // Animation d'entrée rapide et enchaînée des éléments de la pré-home
        animateEntrance('#pre-home .font-size-xl, #pre-home .font-size-l, #pre-home .cta-container, #pre-home .font-emphase, #pre-home .pre-home-footer img', {
            yPercent: 100,       // Translation Y importante vers le haut
            duration: 0.5,      // Animation rapide
            stagger: 0.18,       // Chaînage ultra-rapide (superposition)
            ease: "power3.out"  // Ease dynamique
        });

        // Première visite : le carton est affiché, on attend le clic sur Explorer
        if (explorerBtn) {
            explorerBtn.addEventListener('click', (e) => {
                e.preventDefault();

                // Sauvegarde de l'état vu dans un cookie de session et sessionStorage
                document.cookie = "prehome_seen=1; path=/";
                sessionStorage.setItem('prehome_seen', '1');

                // Fondu de disparition du carton pré-home
                gsap.to(prehome, {
                    opacity: 0,
                    duration: 0.6,
                    ease: "power2.inOut",
                    onComplete: () => {
                        prehome.style.display = 'none';
                        document.body.classList.remove('has-prehome');
                    }
                });

                // Lancement de l'animation d'entrée
                if (productSwiper) {
                    initSliderWhenReady(productSwiper, productSwiper.activeIndex);
                } else if (document.querySelector('#home-scroll-container')) {
                    initHomeScrollTriggerWhenReady();
                }
            });
        }
    } else {
        // Visites suivantes : le carton est déjà masqué
        if (productSwiper) {
            initSliderWhenReady(productSwiper, productSwiper.activeIndex);
        } else if (document.querySelector('#home-scroll-container')) {
            initHomeScrollTriggerWhenReady();
        }
    }

    const cardsSwiperEl = document.querySelector('.cards-swiper');
    if (cardsSwiperEl && typeof Swiper !== 'undefined') {
        new Swiper(cardsSwiperEl, {
            slidesPerView: 1.5, spaceBetween: 8, centeredSlides: false,
            navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
            breakpoints: {
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
                1280: { slidesPerView: 4 }
            }
        });
    }

    // ========================================================================
    // INTEGRATION 3D SKETCHFAB
    // ========================================================================
    const iframe = document.getElementById('sketchfab-api-frame');
    if (iframe) {
        const uid = iframe.getAttribute('data-uid');
        console.log('ICI');
        if (uid) {
            const client = new Sketchfab('1.12.1', iframe);
            let apiInstance = null;

            client.init(uid, {
                success: function onSuccess(api) {
                    apiInstance = api;
                    api.start();

                    // --- ÉTAPE 1 & 2 : DISPARITION DU LOADER (300ms) ---
                    console.log("1. Modèle prêt. Déclenchement du fondu du loader.");
                    api.addEventListener('viewerready', function() {
                        console.log('viewer ready');
                        // 1. ANCHORAGE : On récupère la position "Hero" du parfum
                        api.getCameraLookAt(function(err, camera) {
                            console.log('camera change');
                            if (!err) {
                                const defaultEye = camera.position;
                                const target = camera.target;

                                // Mathématiques du point de départ décalé...
                                //angle à -0.60 (~34°) pour effet plus ample
                                const angle = -0.42;
                                const vx = defaultEye[0] - target[0];
                                const vy = defaultEye[1] - target[1];
                                const vz = defaultEye[2] - target[2];
                                const startX = target[0] + (vx * Math.cos(angle) - vy * Math.sin(angle));
                                const startY = target[1] + (vx * Math.sin(angle) + vy * Math.cos(angle));
                                const startZ = target[2] + (vz * 1.04);

                                // 2. TÉLÉPORTATION INVISIBLE (0s)
                                api.setCameraLookAt([startX, startY, startZ], target, 0);

                                // 3. EFFACEMENT DU LOADER CUSTOM (300ms)
                                const loader = document.getElementById('sketchfab-loader');
                                if (loader) {
                                    loader.classList.add('opacity-0', 'pointer-events-none');
                                    setTimeout(() => loader.remove(), 300);
                                }

                                // 4. APPARITION DE L'IFRAME & TRAVELLING (3.5s)
                                setTimeout(() => {
                                    if (iframe) iframe.classList.remove('opacity-0');

                                    const iconContainer = document.querySelector('.prod_icon_container');
                                    if (iconContainer) {
                                        iconContainer.classList.add('is-visible');
                                    }

                                    setTimeout(() => {
                                        api.setCameraLookAt(defaultEye, target, 2); 
                                        console.log('camera look at');
                                    }, 100);
                                }, 300);
                            }
                        });


                        // --- RESTE DE TES FONCTIONS (Inchangé) ---
                        // Synchronisation des titres
                        api.getAnnotationList(function(err, annotations) {
                            if (!err && annotations.length > 0) {
                                const accordionItems = document.querySelectorAll('.product-accordions .accordion_content');
                                accordionItems.forEach(item => {
                                    const type = item.dataset.type;
                                    const numeroStr = item.dataset.numero;
                                    const titleEl = item.querySelector('button span.text-base');
                                    if (type === 'annotation' && numeroStr && titleEl) {
                                        const annotIndex = parseInt(numeroStr, 10) - 1;
                                        if (annotIndex >= 0 && annotIndex < annotations.length) {
                                            api.updateAnnotation(annotIndex, {
                                                title: titleEl.innerText.trim(),
                                                content: '' 
                                            });
                                        }
                                    }
                                });
                            }
                        });

                        // Gestion du clic annotation
                        api.addEventListener('annotationSelect', function(annotIndex) {
                            if (annotIndex !== -1) {
                                const targetNumero = (annotIndex + 1).toString();
                                const matchingItem = document.querySelector(`.product-accordions .accordion_content[data-type="annotation"][data-numero="${targetNumero}"]`);
                                if (matchingItem) {
                                    const accIndex = parseInt(matchingItem.dataset.index, 10);
                                    window.dispatchEvent(new CustomEvent('sketchfab-point-clicked', { detail: accIndex }));
                                }
                            }
                        });
                    });
                },
                error: function onError() {
                    console.error("Erreur lors du chargement de l'API Sketchfab");
                },
                // 
                //animation au chargement
                autostart: 0,
                animation_autoplay: 0, // Lance l'animation direct
                animation_loop: 0,     // Ne boucle pas, se joue une seule fois
                //
                scrollwheel: 0,
                autospin: 0,
                camera: 0,
                transparent: 1,       // Fond transparent
                ui_annotations: 0,    // Masque le menu d'annotations natif
                ui_stop: 0,           // Masque le bouton stop
                preload: 1,           // Force le préchargement

                // Suppression des éléments d'interface :
                ui_controls: 0,
                ui_start: 0,
                ui_infos: 0,          // Masque le titre et le nom de l'auteur
                ui_settings: 0,       // Masque l'engrenage des réglages
                ui_help: 0,           // Masque le point d'interrogation
                ui_vr: 0,             // Masque le bouton VR
                ui_fullscreen: 0,     // Masque le bouton plein écran
                ui_animations: 0,     // Masque le contrôleur d'animation
                ui_hint: 0,           // Masque le tooltip "Click & drag to rotate" au démarrage
                ui_title: 0,
                ui_author: 0,
                ui_watermark: 0       // Masque le logo Sketchfab en bas à gauche
            });

            // Helper function to trigger subtle glow animation on 3D icons
            function triggerGlowEffect(type) {
                const selector = type === 'cart' ? '.icon-shop' : '.icon-prices';
                const iconEl = document.querySelector(selector);
                if (iconEl) {
                    iconEl.classList.remove('glow-active');
                    void iconEl.offsetWidth; // Force reflow
                    iconEl.classList.add('glow-active');
                }
            }

            // Helper function to rotate Sketchfab camera by +20 degrees around Z axis (yaw)
            function rotateSketchfabCamera() {
                if (!apiInstance) return;
                apiInstance.getCameraLookAt(function(err, camera) {
                    if (err) {
                        console.error("Error getting camera", err);
                        return;
                    }
                    const position = camera.position;
                    const target = camera.target;
                    
                    const dx = position[0] - target[0];
                    const dy = position[1] - target[1];
                    
                    const r = Math.sqrt(dx * dx + dy * dy);
                    const angle = Math.atan2(dy, dx);
                    
                    const newAngle = angle + (20 * Math.PI / 180);
                    const newPosition = [
                        target[0] + r * Math.cos(newAngle),
                        target[1] + r * Math.sin(newAngle),
                        position[2]
                    ];
                    
                    apiInstance.setCameraLookAt(newPosition, target, 1.0);
                });
            }

            // Functions to activate cart/prices and update visual states
            function activateCart() {
                const accordion = document.querySelector('.product-accordions .accordion_content[data-type="cart"]');
                if (accordion) {
                    const index = parseInt(accordion.dataset.index, 10);
                    window.dispatchEvent(new CustomEvent('sketchfab-point-clicked', { detail: index }));
                    rotateSketchfabCamera();
                    //triggerGlowEffect('cart');
                }
            }

            function activatePrices() {
                const accordion = document.querySelector('.product-accordions .accordion_content[data-type="euro"]');
                if (accordion) {
                    const index = parseInt(accordion.dataset.index, 10);
                    window.dispatchEvent(new CustomEvent('sketchfab-point-clicked', { detail: index }));
                    rotateSketchfabCamera();
                    //triggerGlowEffect('euro');
                }
            }

            // ACCORDÉON -> 3D
            window.addEventListener('accordion-clicked', (event) => {
                if (apiInstance) {
                    const accIndex = event.detail.index;
                    const item = document.querySelector(`.product-accordions .accordion_content[data-index="${accIndex}"]`);
                    if (item) {
                        const type = item.dataset.type;
                        const numeroStr = item.dataset.numero;
                        if (type === 'annotation' && numeroStr) {
                            const annotIndex = parseInt(numeroStr, 10) - 1;
                            if (annotIndex >= 0) {
                                apiInstance.gotoAnnotation(annotIndex);
                            }
                        } else if (type === 'cart') {
                            rotateSketchfabCamera();
                            //triggerGlowEffect('cart');
                        } else if (type === 'euro') {
                            rotateSketchfabCamera();
                            //triggerGlowEffect('euro');
                        }
                    }
                }
            });

            // Click on icons -> open accordion and rotate camera (open only, no toggle)
            const iconShop = document.querySelector('.icon-shop');
            const iconPrices = document.querySelector('.icon-prices');

            if (iconShop) {
                iconShop.addEventListener('click', activateCart);
            }

            if (iconPrices) {
                iconPrices.addEventListener('click', activatePrices);
            }
        }
    }

    // --- Global quizApp function for AlpineJS ---
    window.quizApp = function(questions, productId, results) {
        return {
            questions: questions,
            productId: productId,
            results: results || {},
            currentIndex: 0,
            selectedChoice: null,
            answered: false,
            finished: false,
            showExplanation: false,
            score: 0,
            getCurrentQuestion() {
                return this.questions[this.currentIndex];
            },
            getScorePercent() {
                if (!this.questions.length) return 0;
                return Math.round((this.score / this.questions.length) * 100);
            },
            getResultText() {
                const pct = this.getScorePercent();
                if (pct <= 33) return this.results.result_1 || '';
                if (pct <= 66) return this.results.result_2 || '';
                return this.results.result_3 || '';
            },
            selectChoice(key) {
                if (this.answered) return;
                this.selectedChoice = key;
                this.answered = true;
                if (key === this.getCurrentQuestion().correct) {
                    this.score++;
                }
            },
            getChoiceClass(key) {
                if (!this.answered) {
                    return '';
                }
                const correct = this.getCurrentQuestion().correct;
                if (key === correct) {
                    return 'good-response';
                }
                if (this.selectedChoice === key) {
                    return 'bad-response';
                }
                return 'inactive';
            },
            nextQuestion() {
                if (!this.answered) return;
                window.dispatchEvent(new CustomEvent('quiz-fade-out'));
                setTimeout(() => {
                    if (!this.showExplanation) {
                        // Switch to explanation view
                        this.showExplanation = true;
                        setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('quiz-next-question'));
                        }, 150);
                    } else {
                        // Switch back to question view
                        this.showExplanation = false;
                        if (this.currentIndex < this.questions.length - 1) {
                            this.currentIndex++;
                            this.selectedChoice = null;
                            this.answered = false;
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('quiz-next-question'));
                            }, 150);
                        } else {
                            // Finish quiz and show result screen
                            this.finished = true;
                            // Save to localStorage when result is shown
                            localStorage.setItem('dossier_resolved_' + this.productId, 'true');
                            // Dispatch custom event to notify other cards to update
                            window.dispatchEvent(new CustomEvent('dossier-resolved-' + this.productId));
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('quiz-next-question'));
                            }, 150);
                        }
                    }
                }, 600);
            },
            isPerfect() {
                return this.score === this.questions.length;
            },
            restart() {
                window.dispatchEvent(new CustomEvent('quiz-fade-out'));
                setTimeout(() => {
                    this.currentIndex = 0;
                    this.selectedChoice = null;
                    this.answered = false;
                    this.finished = false;
                    this.showExplanation = false;
                    this.score = 0;
                    localStorage.removeItem('dossier_resolved_' + this.productId);
                    window.dispatchEvent(new CustomEvent('dossier-reset-' + this.productId));
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('quiz-next-question'));
                    }, 150);
                }, 600);
            }
        };
    };

    // ========================================================================
    // PAGE SPECIFIC GSAP ANIMATIONS
    // ========================================================================
    const hasHomeScroll = document.querySelector('#home-scroll-container') !== null;
    if (!hasHomeScroll) {
        initHomeAnimations();
        initProductAnimations();
        initQuizAnimations();
        initCardsAnimations();
    }

    // Démarrer AlpineJS à la toute fin
    Alpine.start();
}

// ============================================================================
// GSAP ANIMATIONS DEFINITIONS
// ============================================================================

function initHomeAnimations() {
    // 1. Intro content animation
    const introContent = document.querySelector('#intro-content .main-wysiwyg-content');
    if (introContent) {
        gsap.fromTo(introContent.children, 
            { y: 30, opacity: 0 },
            {
                scrollTrigger: {
                    trigger: '#intro-content',
                    start: "top 80%",
                    toggleActions: "play none none none",
                    onEnter: () => console.log("[GSAP] #intro-content entered"),
                    onLeave: () => console.log("[GSAP] #intro-content left"),
                    onEnterBack: () => console.log("[GSAP] #intro-content entered back"),
                    onLeaveBack: () => console.log("[GSAP] #intro-content left back")
                },
                y: 0,
                opacity: 1,
                stagger: 0.15,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }
        );
    }

    // 2. Quiz callout section
    const quizCallout = document.querySelector('#quiz-callout');
    if (quizCallout) {
        const bgShape = quizCallout.querySelector('.quiz-bg-shape');
        const elementsToAnimate = quizCallout.querySelectorAll('.quiz-title, .quiz-intro-content, .btn-cta');

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: quizCallout,
                start: "top 80%",
                toggleActions: "play none none none",
                onEnter: () => console.log("[GSAP] #quiz-callout entered"),
                onLeave: () => console.log("[GSAP] #quiz-callout left"),
                onEnterBack: () => console.log("[GSAP] #quiz-callout entered back"),
                onLeaveBack: () => console.log("[GSAP] #quiz-callout left back")
            }
        });

        if (bgShape) {
            gsap.set(bgShape, { height: 0, top: "auto", bottom: 0 });
            tl.to(bgShape, {
                height: "100%",
                duration: 1.2,
                ease: "power2.out"
            });
        }

        if (elementsToAnimate.length > 0) {
            tl.fromTo(elementsToAnimate, 
                { y: 30, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    stagger: 0.15,
                    duration: 0.6,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.2"
            );
        }
    }

    // 3. Key figures / Cards section title
    const keyFiguresTitle = document.querySelector('.key-figures-section h2.font-emphase');
    if (keyFiguresTitle) {
        gsap.fromTo(keyFiguresTitle, 
            { y: 35, opacity: 0 },
            {
                scrollTrigger: {
                    trigger: keyFiguresTitle,
                    start: "top 85%",
                    toggleActions: "play none none none",
                    onEnter: () => console.log("[GSAP] Key Figures Title entered"),
                    onLeave: () => console.log("[GSAP] Key Figures Title left"),
                    onEnterBack: () => console.log("[GSAP] Key Figures Title entered back"),
                    onLeaveBack: () => console.log("[GSAP] Key Figures Title left back")
                },
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }
        );
    }

    // 4. Cards grid animation
    const cards = document.querySelectorAll('.key-figures-section .card-item');
    cards.forEach(card => {
        const cardTl = gsap.timeline({
            scrollTrigger: {
                trigger: card,
                start: "top 85%",
                toggleActions: "play none none none",
                onEnter: () => console.log("[GSAP] Card item entered", card.querySelector('.card-title')?.textContent || card.querySelector('h4')?.textContent || ''),
                onLeave: () => console.log("[GSAP] Card item left", card.querySelector('.card-title')?.textContent || card.querySelector('h4')?.textContent || ''),
                onEnterBack: () => console.log("[GSAP] Card item entered back", card.querySelector('.card-title')?.textContent || card.querySelector('h4')?.textContent || ''),
                onLeaveBack: () => console.log("[GSAP] Card item left back", card.querySelector('.card-title')?.textContent || card.querySelector('h4')?.textContent || '')
            }
        });

        cardTl.fromTo(card, 
            { y: 40, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }
        );

        const subElements = card.querySelectorAll([
            '.card-header-flex',
            '.card-text-content',
            '.card-single-icone-wrapper',
            '.card-footer-icones',
            '.card-logo-wrapper',
            '.card-action'
        ].join(','));

        if (subElements.length > 0) {
            cardTl.fromTo(subElements, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    stagger: 0.1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.4"
            );
        }

        const countPath = card.querySelector('.card-type-count svg path.text-current');
        if (countPath) {
            const targetVal = parseFloat(countPath.getAttribute('stroke-dasharray')) || 100;
            countPath.setAttribute('stroke-dasharray', '0, 100');

            const helper = { val: 0 };
            cardTl.to(helper, {
                val: targetVal,
                duration: 1.2,
                ease: "power2.out",
                onUpdate: () => {
                    countPath.setAttribute('stroke-dasharray', `${helper.val.toFixed(1)}, 100`);
                }
            }, "-=0.4");
        }
    });
}

function initProductAnimations() {
    // 1. Product info panel
    const panel = document.querySelector('.product-info-panel');
    if (panel) {
        const title = panel.querySelector('.product-info-title');
        const accordions = panel.querySelectorAll('.product-accordions .accordion_content');
        const cta = panel.querySelector('.btn-cta');

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: panel,
                start: "top 80%",
                toggleActions: "play none none none"
            }
        });

        if (title) {
            tl.fromTo(title, 
                { y: 25, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.6,
                    ease: "power2.out",
                    clearProps: "transform"
                }
            );
        }

        if (accordions.length > 0) {
            tl.fromTo(accordions, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    stagger: 0.1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.3"
            );
        }

        if (cta) {
            tl.fromTo(cta, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.2"
            );

            cta.addEventListener('click', (e) => {
                e.preventDefault();
                const target = document.querySelector('#prod-video');
                if (target) {
                    gsap.to(window, {
                        scrollTo: { y: target, offsetY: 90 },
                        duration: 1.0,
                        ease: "power2.inOut"
                    });
                }
            });
        }
    }

    // 2. Video wysiwyg content
    const videoContent = document.querySelector('.video-wysiwyg-content');
    if (videoContent) {
        gsap.fromTo(videoContent.querySelectorAll('h2, p'), 
            { y: 30, opacity: 0 },
            {
                scrollTrigger: {
                    trigger: videoContent,
                    start: "top 80%",
                    toggleActions: "play none none none"
                },
                y: 0,
                opacity: 1,
                stagger: 0.15,
                duration: 0.8,
                ease: "power2.out",
                clearProps: "transform"
            }
        );
    }
}

function initQuizAnimations() {
    const quizContainer = document.querySelector('.quiz-container');
    if (!quizContainer) return;

    function animateCurrentQuestion() {
        const question = quizContainer.querySelector('.quiz-question');
        const answers = quizContainer.querySelectorAll('.answers-container .answer');
        const explanation = quizContainer.querySelector('.quiz-explanation-content');
        const resultScreen = quizContainer.querySelector('.quiz-result-screen');
        const counter = quizContainer.querySelector('.quiz-counter');

        if (question) gsap.killTweensOf(question);
        if (answers.length > 0) gsap.killTweensOf(answers);
        if (explanation) gsap.killTweensOf(explanation);
        if (resultScreen) gsap.killTweensOf(resultScreen);
        if (counter) gsap.killTweensOf(counter);

        const tl = gsap.timeline();

        if (counter) {
            tl.fromTo(counter, 
                { y: 20, opacity: 0 },
                {
                    y: 0,
                    opacity: 0.6,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                }
            );
        }

        if (question) {
            tl.fromTo(question, 
                { y: 20, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                },
                counter ? "-=0.3" : undefined
            );
        }

        // Animate explanation content if it is visible
        if (explanation && window.getComputedStyle(explanation).display !== 'none') {
            tl.fromTo(explanation, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                },
                "-=0.3"
            );
        }

        // Animate answers if they are visible
        if (answers.length > 0 && window.getComputedStyle(answers[0]).display !== 'none') {
            tl.fromTo(answers, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    stagger: 0.08,
                    duration: 0.4,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.3"
            );
        }

        // Animate result screen elements if visible
        if (resultScreen && window.getComputedStyle(resultScreen).display !== 'none') {
            gsap.set(resultScreen, { opacity: 1 }); // Clear parent opacity to allow children to show
            const resultChildren = resultScreen.children;
            tl.fromTo(resultChildren, 
                { y: 15, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    stagger: 0.1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                },
                "-=0.3"
            );
        }
    }

    const title = quizContainer.querySelector('.quiz-title');
    const initialTl = gsap.timeline({
        scrollTrigger: {
            trigger: quizContainer,
            start: "top 80%",
            toggleActions: "play none none none"
        }
    });

    if (title) {
        initialTl.fromTo(title, 
            { y: 20, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                clearProps: "transform"
            }
        );
    }

    initialTl.add(() => {
        animateCurrentQuestion();
    }, "+=0.3");

    let startHeight = 0;

    window.addEventListener('quiz-next-question', () => {
        // Measure new height
        gsap.set(quizContainer, { height: "auto" });
        const endHeight = quizContainer.offsetHeight;
        const currentStart = startHeight || endHeight;

        // Animate height from currentStart to endHeight
        gsap.fromTo(quizContainer, 
            { height: currentStart },
            {
                height: endHeight,
                duration: 0.4,
                ease: "power2.out",
                onComplete: () => {
                    gsap.set(quizContainer, { clearProps: "height,overflow" });
                }
            }
        );

        animateCurrentQuestion();
    });

    window.addEventListener('quiz-fade-out', () => {
        const question = quizContainer.querySelector('.quiz-question');
        const answers = quizContainer.querySelectorAll('.answers-container .answer');
        const explanation = quizContainer.querySelector('.quiz-explanation-content');
        const resultScreen = quizContainer.querySelector('.quiz-result-screen');
        const counter = quizContainer.querySelector('.quiz-counter');

        if (question) gsap.killTweensOf(question);
        if (answers.length > 0) gsap.killTweensOf(answers);
        if (explanation) gsap.killTweensOf(explanation);
        if (resultScreen) gsap.killTweensOf(resultScreen);
        if (counter) gsap.killTweensOf(counter);

        // Record and lock height to prevent jump
        startHeight = quizContainer.offsetHeight;
        gsap.set(quizContainer, { height: startHeight, overflow: "hidden" });

        const elements = [];
        if (counter) elements.push(counter);
        if (question) elements.push(question);
        
        if (explanation && window.getComputedStyle(explanation).display !== 'none') {
            elements.push(explanation);
        }
        if (answers.length > 0 && window.getComputedStyle(answers[0]).display !== 'none') {
            answers.forEach(a => elements.push(a));
        }
        if (resultScreen && window.getComputedStyle(resultScreen).display !== 'none') {
            elements.push(resultScreen);
        }

        if (elements.length > 0) {
            gsap.to(elements, {
                y: -15,
                opacity: 0,
                duration: 0.25,
                ease: "power2.in"
            });
        }
    });
}

function initCardsAnimations() {
    const pillarHeader = document.querySelector('.pillar-header');
    if (pillarHeader) {
        const h1 = pillarHeader.querySelector('h1');
        const intro = pillarHeader.querySelector('.pillar-intro-content');

        const tl = gsap.timeline();

        if (h1) {
            tl.fromTo(h1, 
                { y: 25, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.6,
                    ease: "power2.out",
                    clearProps: "transform"
                }
            );
        }

        if (intro) {
            tl.fromTo(intro, 
                { y: 20, opacity: 0 },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.5,
                    ease: "power2.out",
                    clearProps: "transform"
                }, 
                "-=0.3"
            );
        }
    }

    ScrollTrigger.batch('.products-grid-section .product-card, .quizzes-grid-section .product-card, .cards-slider-container .product-card', {
        start: "top 90%",
        once: true,
        onEnter: batch => {
            batch.forEach((card, index) => {
                const header = card.querySelector('.card-header');
                const image = card.querySelector('.card-image');
                const cta = card.querySelector('.btn-cta');

                const cardTl = gsap.timeline({
                    delay: index * 0.15
                });

                // 1. Animate card base
                cardTl.fromTo(card, 
                    { y: 30, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 0.6,
                        ease: "power2.out",
                        clearProps: "transform"
                    }
                );

                // 2. Animate inner header
                if (header) {
                    cardTl.fromTo(header,
                        { y: 15, opacity: 0 },
                        {
                            y: 0,
                            opacity: 1,
                            duration: 0.5,
                            ease: "power2.out",
                            clearProps: "transform"
                        },
                        "-=0.4"
                    );
                }

                // 3. Animate inner image (y & opacity + small scale)
                if (image) {
                    cardTl.fromTo(image,
                        { y: 15, scale: 0.95, opacity: 0 },
                        {
                            y: 0,
                            scale: 1,
                            opacity: 1,
                            duration: 0.5,
                            ease: "power2.out",
                            clearProps: "transform"
                        },
                        "-=0.4"
                    );
                }

                // 4. Animate inner CTA button
                if (cta) {
                    cardTl.fromTo(cta,
                        { y: 15, opacity: 0 },
                        {
                            y: 0,
                            opacity: 1,
                            duration: 0.5,
                            ease: "power2.out",
                            clearProps: "transform"
                        },
                        "-=0.4"
                    );
                }
            });
        }
    });
}

// Lancement au chargement
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTheme);
} else {
    initTheme();
}